const DEVICE_ID_KEY = 'alea.device.id';

export type DevicePlatform = 'web' | 'ios' | 'android';

export interface DeviceIdentitySnapshot {
  deviceId: string;
  label: string;
  platform: DevicePlatform;
  userAgent: string;
}

function readStoredDeviceId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const value = localStorage.getItem(DEVICE_ID_KEY)?.trim();
  return value || null;
}

function writeStoredDeviceId(deviceId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DEVICE_ID_KEY, deviceId);
}

export function getOrCreateDeviceId(): string {
  const existing = readStoredDeviceId();
  if (existing) return existing;
  const deviceId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  writeStoredDeviceId(deviceId);
  return deviceId;
}

function parseBrowserLabel(userAgent: string): string {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'Safari · iOS';
  if (/Android/i.test(userAgent)) return 'Chrome · Android';
  if (/Edg\//i.test(userAgent)) return 'Edge';
  if (/Chrome\//i.test(userAgent)) return 'Chrome';
  if (/Firefox\//i.test(userAgent)) return 'Firefox';
  if (/Safari\//i.test(userAgent)) return 'Safari';
  return 'Browser';
}

export function buildDeviceLabel(platform: DevicePlatform, userAgent: string): string {
  if (platform === 'ios') return 'iPhone / iPad';
  if (platform === 'android') return 'Android';
  return parseBrowserLabel(userAgent);
}

/**
 * Collects device identity snapshot.
 * Platform must be provided by the caller (from adapter).
 */
export function collectDeviceIdentity(platform: DevicePlatform): DeviceIdentitySnapshot {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return {
    deviceId: getOrCreateDeviceId(),
    label: buildDeviceLabel(platform, userAgent),
    platform,
    userAgent: userAgent.slice(0, 280),
  };
}
